import React, { useState, useEffect, useRef } from 'react';
import { adminService } from '../../../services/adminService';
import AdminTabNav from '../AdminPage/AdminTabNav';
import './AdminGlobalSettingsPage.css';

interface AdminGlobalSettingsPageProps {
  /** When true, render only the form content (no page wrapper or tab nav). Used when embedded in Admin page. */
  embedded?: boolean;
}

const AdminGlobalSettingsPage: React.FC<AdminGlobalSettingsPageProps> = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [outboundId, setOutboundId] = useState('');
  const [inboundId, setInboundId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [voiceIdMale, setVoiceIdMale] = useState('');
  const [voiceIdFemale, setVoiceIdFemale] = useState('');
  const [smsFromPhone, setSmsFromPhone] = useState('');

  const lastSavedSnapshot = useRef<string | null>(null);

  const formSnapshot = () =>
    JSON.stringify({
      outboundId: outboundId.trim(),
      inboundId: inboundId.trim(),
      phoneNumberId: phoneNumberId.trim(),
      voiceIdMale: voiceIdMale.trim(),
      voiceIdFemale: voiceIdFemale.trim(),
      smsFromPhone: smsFromPhone.trim(),
    });

  const hasChanges =
    lastSavedSnapshot.current !== null && formSnapshot() !== lastSavedSnapshot.current;

  const allFieldsFilled =
    outboundId.trim().length > 0 &&
    inboundId.trim().length > 0 &&
    phoneNumberId.trim().length > 0;

  const canSave = hasChanges && allFieldsFilled && !saving;

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const [assistantCfg, smsCfg] = await Promise.all([
        adminService.getGlobalAssistantConfig(),
        adminService.getSmsSettings(),
      ]);

      const o = assistantCfg.global_outbound_assistant_id ?? '';
      const i = assistantCfg.global_inbound_assistant_id ?? '';
      const p = assistantCfg.global_phone_number_id ?? '';
      const vm = assistantCfg.global_voice_id_male ?? '';
      const vf = assistantCfg.global_voice_id_female ?? '';
      const sms = smsCfg.global_sms_from_phone ?? '';
      setOutboundId(o);
      setInboundId(i);
      setPhoneNumberId(p);
      setVoiceIdMale(vm);
      setVoiceIdFemale(vf);
      setSmsFromPhone(sms);

      lastSavedSnapshot.current = JSON.stringify({
        outboundId: o,
        inboundId: i,
        phoneNumberId: p,
        voiceIdMale: vm,
        voiceIdFemale: vf,
        smsFromPhone: sms,
      });
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : 'Failed to load global assistant configuration';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await Promise.all([
        adminService.updateGlobalAssistantConfig({
          global_outbound_assistant_id: outboundId.trim(),
          global_inbound_assistant_id: inboundId.trim(),
          global_phone_number_id: phoneNumberId.trim(),
          global_voice_id_male: voiceIdMale.trim(),
          global_voice_id_female: voiceIdFemale.trim(),
        }),
        adminService.updateSmsSettings({
          global_sms_from_phone: smsFromPhone.trim(),
        }),
      ]);

      setSuccessMessage('Global assistant configuration saved successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadConfig();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : 'Failed to update global assistant configuration';
      setError(String(message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'admin-global-settings-loading admin-global-settings-loading-embedded' : 'admin-global-settings-loading'}>
        <div className="admin-global-settings-loading-spinner" />
      </div>
    );
  }

  const content = (
    <div className="admin-global-settings-container">
      {!embedded && <AdminTabNav />}

        {error && (
          <div className="admin-global-settings-alert admin-global-settings-alert-error">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="admin-global-settings-alert admin-global-settings-alert-success">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        )}

        {hasChanges && (
          <div className="admin-global-settings-alert admin-global-settings-alert-unsaved">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            You have unsaved changes. Save before leaving or you may lose them.
          </div>
        )}

        <div className="admin-global-settings-content">
          <div className="admin-global-settings-section">
            <h2 className="admin-global-settings-section-title">Assistant IDs</h2>
            <div className="admin-global-settings-grid">
              <div className="admin-global-settings-field">
                <label className="admin-global-settings-label">
                  Global Outbound Assistant ID <span className="admin-global-settings-required">*</span>
                </label>
                <input
                  type="text"
                  className="admin-global-settings-input"
                  value={outboundId}
                  onChange={(e) => setOutboundId(e.target.value)}
                  placeholder="e.g. c9339119-006a-4430-b109-38da05b00964"
                />
                <p className="admin-global-settings-help">Voice Gateway agent ID for outbound calls (campaigns)</p>
              </div>
              <div className="admin-global-settings-field">
                <label className="admin-global-settings-label">
                  Global Inbound Assistant ID <span className="admin-global-settings-required">*</span>
                </label>
                <input
                  type="text"
                  className="admin-global-settings-input"
                  value={inboundId}
                  onChange={(e) => setInboundId(e.target.value)}
                  placeholder="e.g. 6bb5d0b9-2284-4916-b430-062471b36108"
                />
                <p className="admin-global-settings-help">Voice Gateway agent ID for inbound calls</p>
              </div>
              <div className="admin-global-settings-field">
                <label className="admin-global-settings-label">
                  Global Phone Number ID <span className="admin-global-settings-required">*</span>
                </label>
                <input
                  type="text"
                  className="admin-global-settings-input"
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                  placeholder="e.g. +61240727060"
                />
                <p className="admin-global-settings-help">Default outbound phone number (E.164 format) for users without a dedicated pool number</p>
              </div>
            </div>
          </div>
          <div className="admin-global-settings-section">
            <h2 className="admin-global-settings-section-title">SMS (optional)</h2>
            <div className="admin-global-settings-grid">
              <div className="admin-global-settings-field">
                <label className="admin-global-settings-label">Default SMS From Number (fallback)</label>
                <input
                  type="text"
                  className="admin-global-settings-input"
                  value={smsFromPhone}
                  onChange={(e) => setSmsFromPhone(e.target.value)}
                  placeholder="e.g. +61412345678"
                />
                <p className="admin-global-settings-help">
                  Used only when a user has no assigned pool number. Must be E.164 format.
                </p>
              </div>
            </div>
          </div>
          <div className="admin-global-settings-section">
            <h2 className="admin-global-settings-section-title">Voice IDs (optional)</h2>
            <p className="admin-global-settings-help" style={{ marginBottom: '1rem' }}>
              Voice IDs for the run voice dropdown. Users choose Male or Female when creating a run.
            </p>
            <div className="admin-global-settings-grid">
              <div className="admin-global-settings-field">
                <label className="admin-global-settings-label">Men voice ID</label>
                <input
                  type="text"
                  className="admin-global-settings-input"
                  value={voiceIdMale}
                  onChange={(e) => setVoiceIdMale(e.target.value)}
                  placeholder="Voice ID for male voice"
                />
              </div>
              <div className="admin-global-settings-field">
                <label className="admin-global-settings-label">Women voice ID</label>
                <input
                  type="text"
                  className="admin-global-settings-input"
                  value={voiceIdFemale}
                  onChange={(e) => setVoiceIdFemale(e.target.value)}
                  placeholder="Voice ID for female voice"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="admin-global-settings-footer">
          <button
            className="admin-global-settings-btn-save"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? (
              <>
                <div className="admin-global-settings-btn-spinner" />
                Saving...
              </>
            ) : (
              <>
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
  );

  if (embedded) return content;
  return (
    <div className="admin-global-settings-page">
      <div className="admin-global-settings-bg">
        <div className="admin-global-settings-bg-1" />
        <div className="admin-global-settings-bg-2" />
      </div>
      {content}
    </div>
  );
};

export default AdminGlobalSettingsPage;
